/**
 * The action bar that appears once photos are selected.
 *
 * Floats above the grid rather than replacing the header, so the user never
 * loses sight of what they picked. Destructive actions sit at the far end,
 * away from the ones people reach for reflexively.
 */

import { formatBytes } from "@fc/core";
import { Button } from "../../components/ui/Button.js";
import {
  DropdownMenu,
  type DropdownMenuItem,
} from "../../components/ui/DropdownMenu.js";
import type { Album } from "../album/types.js";
import "./SelectionBar.css";

export interface SelectionBarProps {
  count: number;
  totalBytes: number;
  canDelete: boolean;
  canDownload: boolean;
  canShare: boolean;
  canOrganize: boolean;
  albums: Album[];
  onSelectAll: () => void;
  onShare: () => void;
  onDownload: () => void;
  onClear: () => void;
  onDelete: () => void;
  /** `null` takes the files out of whatever album they are in. */
  onMove: (folderId: string | null) => void;
  isDeleting: boolean;
  isMoving: boolean;
}

/*
 * Glyphs for the selection bar.
 *
 * Five labelled buttons and a count do not fit across a phone — the bar was
 * wrapping into a block tall enough to cover the photographs it was acting on,
 * which is a strange thing for a toolbar to do to its own subject. On a narrow
 * screen the labels are visually hidden and these carry the meaning; the
 * labels stay in the accessibility tree, so nothing is lost to a screen
 * reader.
 */
const selectionIcon = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const SelectAllIcon = () => (
  <svg {...selectionIcon}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
    <path d="M8 12.2l2.8 2.8L16.5 9.3" />
  </svg>
);

const AddToAlbumIcon = () => (
  <svg {...selectionIcon}>
    <path d="M3 8.5A2 2 0 0 1 5 6.5h3.2l1.6 2H19a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M12 11.5v5M9.5 14h5" />
  </svg>
);

const DownloadIcon = () => (
  <svg {...selectionIcon}>
    <path d="M12 4v10.5M8 11l4 4 4-4" />
    <path d="M4.5 17.5V19a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-1.5" />
  </svg>
);

const ShareIcon = () => (
  <svg {...selectionIcon}>
    <circle cx="17.5" cy="6" r="2.5" />
    <circle cx="6.5" cy="12" r="2.5" />
    <circle cx="17.5" cy="18" r="2.5" />
    <path d="M8.8 10.8l6.4-3.5M8.8 13.2l6.4 3.5" />
  </svg>
);

const TrashIcon = () => (
  <svg {...selectionIcon}>
    <path d="M4.5 7h15M9.5 7V5.5A1.5 1.5 0 0 1 11 4h2a1.5 1.5 0 0 1 1.5 1.5V7" />
    <path d="M6.5 7l.8 12a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7" />
  </svg>
);
export function SelectionBar({
  count,
  totalBytes,
  canDelete,
  canDownload,
  canShare,
  canOrganize,
  albums,
  onSelectAll,
  onClear,
  onShare,
  onDownload,
  onDelete,
  onMove,
  isDeleting,
  isMoving,
}: SelectionBarProps) {
  const albumItems: DropdownMenuItem[] =
    albums.length === 0
      ? [{ label: "No albums yet", onSelect: () => undefined, disabled: true }]
      : albums.map((album) => ({
          label:
            album.emoji === null ? album.name : `${album.emoji}  ${album.name}`,
          onSelect: () => onMove(album.id),
        }));

  // Always offered, even from the "all photos" view: the selection can contain
  // files that are already filed, and there is nowhere else to unfile them.
  const menuItems: DropdownMenuItem[] = [
    ...albumItems,
    { label: "Remove from album", onSelect: () => onMove(null) },
  ];

  return (
    <div
      className="fc-selection"
      role="toolbar"
      aria-label={`${count} selected`}
    >
      <button
        type="button"
        className="fc-selection__close"
        onClick={onClear}
        aria-label="Clear selection"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      <div className="fc-selection__count">
        <strong className="fc-tabular">{count}</strong>
        <span className="fc-selection__bytes fc-tabular">
          {formatBytes(totalBytes)}
        </span>
      </div>

      <div className="fc-selection__actions">
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<SelectAllIcon />}
          onClick={onSelectAll}
        >
          Select all
        </Button>

        {canOrganize && (
          <DropdownMenu
            align="end"
            menuLabel="Add to album"
            items={menuItems}
            trigger={
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<AddToAlbumIcon />}
                loading={isMoving}
              >
                Add to album
              </Button>
            }
          />
        )}

        {canDownload && (
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<DownloadIcon />}
            onClick={onDownload}
          >
            Download
          </Button>
        )}

        {canShare && (
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<ShareIcon />}
            onClick={onShare}
          >
            Share
          </Button>
        )}

        {canDelete && (
          <Button
            variant="danger"
            size="sm"
            leadingIcon={<TrashIcon />}
            loading={isDeleting}
            onClick={onDelete}
          >
            Move to trash
          </Button>
        )}
      </div>
    </div>
  );
}
